
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { fillCart } from '@/utils/reordering';
import { fireGoogleTag } from '@/utils/google-tag-manager-helpers';

const namespace = 'cart';

@Component<Order>({})
export default class Order extends Vue {
	@Action('setTemporaryItem', { namespace }) private setTemporaryItem!: (temporaryItemPayload: { tempItem: MenuItem|{}, quantity?: number }) => void;
	@Getter('getItems', { namespace }) private getCartItems!: OrderItem[];
	@Getter('getMaxItemsPerCart', { namespace: 'restaurant' }) private maxItemsPerCart!: number;
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;
	@Getter('hidePricing', { namespace: 'restaurant' }) private hidePricing!: boolean;
	@Prop({ type: String, required: true, default: '' }) private id!: string;
	@Prop({ type: Boolean, required: false, default: false }) private isAnyLoading!: boolean;
	@Prop({ type: Object, required: true, default: () => {} }) private order!: ReceiptInfo;

	private showDetails: boolean = false;
	private isLoading: boolean = false;

	private orderAmount: number =
		(this.order.subtotal || 0)
		+ (this.order.serviceCharge || 0)
		+ (this.order.preTaxDiscount || 0)
		+ (this.order.posPreTaxDiscount || 0)
		+ (this.order.tax || 0)
		+ (this.order.deliveryCharge || 0)
		+ (this.order.payment || 0);

	/**
	 * Map of order statuses to their CSS class and i18n label key
	 */
	private readonly statusConfig: Record<string, { class: string; label: string }> = {
		'pending': { class: 'status-yellow', label: 'status_pending' },
		'in-progress': { class: 'status-blue', label: 'status_in_progress' },
		'in_progress': { class: 'status-blue', label: 'status_in_progress' },
		'completed': { class: 'status-green', label: 'status_completed' },
		'cancelled': { class: 'status-red', label: 'status_cancelled' },
		'refunded': { class: 'status-red', label: 'status_refunded' }
	};

	/**
	 * Return the CSS class and i18n label for the order status, or undefined if unrecognized then the tag won't render.
	 *
	 * @return {{ class: string; label: string } | undefined}
	 */
	private get statusInfo(): { class: string; label: string } | undefined {
		return this.statusConfig[this.order.status?.toLowerCase() || ''];
	}

	/**
	 * Show re-order button if the order is from the current restaurant and the location isn't a K12 location
	 *
	 * @return {boolean}
	 */
	private get showReorderButton(): boolean {
		return !!(this.restaurant?.app8_restaurant && this.order.restaurantId && this.restaurant?.app8_restaurant === this.order.restaurantId && !this.restaurant?.k12);
	}

	/**
	 * Calculate the visible discount amount, excluding discounts already included in item prices
	 *
	 * @return {number}
	 */
	private get visibleDiscountAmount(): number {
		const discountAmount = Math.abs((this.order.postTaxDiscount || 0) + (this.order.posPostTaxDiscount || 0));
		const includedDiscount = this.order.discounts.reduce((sum: number, d: ReceiptDiscount) => d.pricesIncludeDiscount ? sum + d.amount : sum, 0);
		return discountAmount - includedDiscount;
	}

	/**
	 * Format the date of the order
	 *
	 * @param {string} utcDate
	 * @return {string}
	 */
	private formatDate(utcDate: string): string {
		const date = DateTime.fromISO(utcDate).setLocale(this.$i18n.locale).toFormat('D');
		const time = DateTime.fromISO(utcDate).setLocale(this.$i18n.locale).toFormat('t');
		return `${date} • ${time}`;
	}

	/**
	 * Show or hide order details
	 * and scroll to the position of that order
	 *
	 * @return {void}
	 */
	private toggleShowReceipt(): void {
		this.showDetails = !this.showDetails;
		if (this.showDetails) {
			setTimeout(() => {
				const receiptElement = document.getElementById('receipt-' + this.id);
				receiptElement && receiptElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
			}, 100);
		}
	}

	/**
	 * Add the items from the order to the cart if possible.
	 *
	 * @return {Promise<void>}
	 */
	private async reorderItems(): Promise<void> {
		// If cart is already full, stop here
		const itemsInCartBefore: number = this.getCartItems.reduce((sum, item) => sum += item.quantity, 0);
		if (itemsInCartBefore === this.maxItemsPerCart) {
			this.$toasted.show(this.$t('profile.past_orders.error_cart_full'), { type: 'error', position: 'top-center' }).goAway(5000);
			return;
		}

		this.isLoading = true;
		this.$emit('loading', true);

		const changeWarningFlag: boolean = await fillCart(this.order.items, itemsInCartBefore);

		this.isLoading = false;
		this.setTemporaryItem({ tempItem: {} });
		this.$emit('loading', false);

		// Check if we actually added items
		const itemsInCartAfter: number = this.getCartItems.reduce((sum, item) => sum += item.quantity, 0);
		const itemsAddedToCart: number = itemsInCartAfter - itemsInCartBefore;
		const itemsInOrder: number = this.order.items.reduce((sum, item) => sum += item.quantity, 0);
		const itemsNotAddedToCart: number = itemsInOrder - itemsAddedToCart;

		fireGoogleTag({
			name: 'reorderCart',
			detail: `${itemsAddedToCart} succeed`,
			specifier: `${itemsNotAddedToCart} attempt`
		});
		if (itemsAddedToCart === 0) {
			this.$toasted.show(this.$t('profile.past_orders.error_reorder_items'), { type: 'error', position: 'top-center' }).goAway(5000);
			return;
		}

		this.redirectToMenu(changeWarningFlag);
	}

	/**
	 * Redirect to the menu with the cart open, and a warning modal about the changes if applicable
	 *
	 * @param {boolean} changeWarning
	 * @return {void}
	 */
	private redirectToMenu(changeWarning: boolean): void {
		const queryParams: { [key: string]: any } = { ...this.$route.query, openCart: 'true' };
		if (changeWarning) {
			queryParams.reorderWarning = 'true';
		}
		this.$router.push({ path: `/${this.restaurant?.slug}`, query: queryParams, hash: this.$route.hash }).catch(() => {});
	}
}
